import { useState } from "react";
import { Link, useLoaderData, type LoaderFunctionArgs } from "react-router";
import { CenteredSkeleton, FieldSkeleton, Lines } from "../components/skeletons.jsx";
import { Deferred, ErrorState } from "../components/states.jsx";
import { useI18n } from "../i18n/index.js";
import { api, type OAuthClientInfo, type WorkspaceSummary } from "../lib/api.js";
import { deferred } from "../lib/deferred.js";
import { apiContext, sessionContext } from "../lib/session.js";
import { lastWorkspaceSlug } from "../lib/workspace.js";
import { Brand, Button, CenteredPage, FormError, LinkButton, Select, Skeleton } from "../ui/index.js";

// A coding agent asking to act as this person (D-0021). The authorization server sends the browser here — to sign in
// first if need be — with a signed query; the person picks the one workspace the agent will work in, and the answer
// goes back through control-api, which sends the browser on to the agent.
export function oauthConsentLoader({ context, request }: LoaderFunctionArgs) {
  const query = new URL(request.url).search.slice(1);
  const clientId = new URLSearchParams(query).get("client_id");
  const me = context.get(sessionContext);
  const client: Promise<OAuthClientInfo | null> =
    clientId && new URLSearchParams(query).has("sig")
      ? context
          .get(apiContext)
          .oauthClient(clientId)
          .catch(() => null)
      : Promise.resolve(null);
  return { query, client: deferred(client), workspaces: me?.workspaces ?? [] };
}

/** Who is asking is on its way: the heading, the workspace picker, what it may do, the two answers. */
export function OAuthConsentSkeleton() {
  const { t } = useI18n();
  return (
    <CenteredSkeleton>
      <div className="dt-form">
        <FieldSkeleton label={t("oauthConsent.workspace")} />
        <Lines count={2} />
        <div className="dt-form__actions" style={{ justifyContent: "flex-end" }}>
          <Skeleton shape="button" width={80} />
          <Skeleton shape="button" width={96} />
        </div>
      </div>
    </CenteredSkeleton>
  );
}

export function OAuthConsentRoute() {
  const { query, client, workspaces } = useLoaderData<typeof oauthConsentLoader>();
  return (
    <Deferred data={client} fallback={<OAuthConsentSkeleton />}>
      {(client) => <Consent query={query} client={client} workspaces={workspaces} />}
    </Deferred>
  );
}

function Consent({ query, client, workspaces }: { query: string; client: OAuthClientInfo | null; workspaces: WorkspaceSummary[] }) {
  const { t } = useI18n();
  const remembered = workspaces.find((w) => w.slug === lastWorkspaceSlug()) ?? workspaces[0];
  const [workspaceId, setWorkspaceId] = useState(remembered?.id ?? "");
  const [busy, setBusy] = useState<"accept" | "deny" | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!client) {
    return (
      <CenteredPage>
        <ErrorState
          title={t("oauthConsent.invalidTitle")}
          message={t("oauthConsent.invalid")}
          action={
            <LinkButton to="/" variant="secondary" size="sm">
              {t("invitation.goToWorkspaces")}
            </LinkButton>
          }
        />
      </CenteredPage>
    );
  }

  const name = client.client_name || t("oauthConsent.unnamed");
  const answer = async (accept: boolean) => {
    setBusy(accept ? "accept" : "deny");
    setError(null);
    try {
      const { redirectTo } = await api.consentToAgent({ accept, oauthQuery: query, ...(accept ? { workspaceId } : {}) });
      window.location.assign(redirectTo);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(null);
    }
  };

  return (
    <CenteredPage>
      <div className="dt-centered__head">
        <Brand size="lg" />
        <div>
          <h1>{t("oauthConsent.title", { client: name })}</h1>
          <p className="dt-muted">{t("oauthConsent.subtitle", { client: name })}</p>
        </div>
      </div>
      {workspaces.length === 0 ? (
        <p>
          <Link to="/workspaces/new">{t("oauthConsent.noWorkspace")}</Link>
        </p>
      ) : (
        <div className="dt-form">
          <div className="dt-field">
            <span className="dt-field__label">{t("oauthConsent.workspace")}</span>
            <Select items={workspaces.map((w) => ({ value: w.id, label: w.name }))} value={workspaceId} onValueChange={setWorkspaceId} aria-label={t("oauthConsent.workspace")} />
          </div>
          <ul className="dt-muted" style={{ margin: 0, paddingInlineStart: "1.2em" }}>
            <li>{t("oauthConsent.can")}</li>
            <li>{t("oauthConsent.cannot")}</li>
          </ul>
          <FormError message={error} />
          <div className="dt-form__actions" style={{ justifyContent: "flex-end" }}>
            <Button variant="ghost" loading={busy === "deny"} disabled={busy !== null} onClick={() => answer(false)}>
              {t("common.cancel")}
            </Button>
            <Button variant="primary" loading={busy === "accept"} disabled={busy !== null || !workspaceId} onClick={() => answer(true)}>
              {t("oauthConsent.connect")}
            </Button>
          </div>
        </div>
      )}
    </CenteredPage>
  );
}
